// web-staking-estimator-frontend/src/hooks/useServiceOptions.js
import { useState, useEffect, useMemo } from "react";

const API = process.env.REACT_APP_API_BASE_URL || "/api";

function uniq(arr) {
  const set = new Set();
  const out = [];
  for (const v of arr) {
    const key = v == null ? "__NULL__" : String(v);
    if (!set.has(key)) {
      set.add(key);
      out.push(v);
    }
  }
  return out;
}

function normOHUG(v) {
  return (v == null ? "" : String(v)).trim().toUpperCase();
}

/**
 * Hook to manage service options (phases, volts, amps, OH/UG)
 * Handles loading services from API and filtering based on selections
 */
export function useServiceOptions({
  category,
  value,
  selectedPoint,
  xfmrOHUGHint,
  cannotEstimateMsg,
}) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ohugOptions, setOhugOptions] = useState([]);
  const [xfmrOHUG, setXfmrOHUG] = useState(null);
  const [optLoading, setOptLoading] = useState(false);
  const [optMsg, setOptMsg] = useState(null);

  const safeCategory = (category || "").toLowerCase();
  const v = value || {};

  // Load ALL services
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API}/assemblies/${safeCategory}/services`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((rows) => {
        if (cancelled) return;
        const all = Array.isArray(rows) ? rows : [];
        setServices(all);
        if (process.env.NODE_ENV === 'development') {
          console.log("[SCF:servicesLoaded]", { count: all.length, category: safeCategory });
        }
      })
      .catch((err) => {
        setServices([]);
        console.warn("[SCF:servicesLoadError]", err?.message || err);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [safeCategory]);

  // Filter services by type
  const filteredByType = useMemo(() => {
    if (!v.svc_type) return services;
    return services.filter((s) => s.svc_type === v.svc_type);
  }, [services, v.svc_type]);

  // Get distinct phases
  const phases = useMemo(() => {
    return uniq(
      filteredByType.map((s) => Number(s.svc_phase)).filter((n) => Number.isFinite(n))
    ).sort((a, b) => a - b);
  }, [filteredByType]);

  // Get distinct volts (filtered by phase if selected)
  const volts = useMemo(() => {
    return uniq(
      filteredByType
        .filter((s) => v.svc_phase == null || Number(s.svc_phase) === Number(v.svc_phase))
        .map((s) => s.svc_volts)
        .filter((x) => (x ?? "").toString().trim() !== "")
    ).sort((a, b) => String(a).localeCompare(String(b)));
  }, [filteredByType, v.svc_phase]);

  // Get distinct amps (filtered by phase and volts if selected)
  const amps = useMemo(() => {
    return uniq(
      filteredByType
        .filter(
          (s) =>
            (v.svc_phase == null || Number(s.svc_phase) === Number(v.svc_phase)) &&
            (!v.svc_volts || s.svc_volts === v.svc_volts)
        )
        .map((s) => Number(s.svc_amps))
        .filter((n) => Number.isFinite(n))
    ).sort((a, b) => a - b);
  }, [filteredByType, v.svc_phase, v.svc_volts]);

  /*
   * Load OH/UG options based on nearest infrastructure
   * User clicks map → gets xfmrOHUGHint (OH/UG from nearest transformer)
   * We filter available services to only show options compatible with that infrastructure
   * Irrigation uses HP, residential/commercial use amps
   */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setOptLoading(true);
      setOptMsg(null);
      try {
        const params = new URLSearchParams();
        if (v.svc_type) params.set("svc_type", v.svc_type);
        if (v.svc_phase != null) params.set("svc_phase", String(v.svc_phase));
        if (v.svc_volts) params.set("svc_volts", v.svc_volts);
        
        // Use HP for irrigation, amps for others
        const isIrrigation = safeCategory === 'irrigation';
        if (isIrrigation) {
          if (v.svc_hp != null) params.set("svc_hp", String(v.svc_hp));
        } else {
          if (v.svc_amps != null) params.set("svc_amps", String(v.svc_amps));
        }

        if (
          selectedPoint?.wkid &&
          Number.isFinite(selectedPoint?.x) &&
          Number.isFinite(selectedPoint?.y)
        ) {
          params.set("wkid", String(selectedPoint.wkid));
          params.set("x", String(selectedPoint.x));
          params.set("y", String(selectedPoint.y));
        }

        const url = `${API}/assemblies/${safeCategory}/services?${params.toString()}`;
        const r = await fetch(url);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const rows = await r.json();

        const rowsFiltered = Array.isArray(rows) ? rows : [];
        /*
         * Filter services to match user selections
         * Irrigation: backend already filtered by HP (finds smallest assembly >= requested HP)
         * Residential/Commercial: filter by exact amps match
         */
        let base = rowsFiltered.filter((s) => {
          // Basic filters
          if (v.svc_type && s.svc_type !== v.svc_type) return false;
          if (v.svc_phase != null && Number(s.svc_phase) !== Number(v.svc_phase)) return false;
          if (v.svc_volts && s.svc_volts !== v.svc_volts) return false;
          
          // Conditional: HP for irrigation, amps for others
          if (isIrrigation) {
            // For irrigation, backend already filtered by HP (svc_max_hp >= requested)
            // No additional filtering needed here
            return true;
          } else {
            // For residential/commercial, filter by amps
            if (v.svc_amps != null && Number(s.svc_amps) !== Number(v.svc_amps)) return false;
            return true;
          }
        });

        const need = normOHUG(xfmrOHUGHint);
        if (need) {
          base = base.filter((s) => normOHUG(s.xfmr_ohug) === need);
        }

        const ohug = uniq(base.map((s) => normOHUG(s.svc_ohug))).filter(
          (x) => (x || "").trim() !== ""
        );

        if (!cancelled) {
          setOhugOptions(ohug);
          setXfmrOHUG(need || null);
          setOptMsg(ohug.length ? null : "No options");

          if (process.env.NODE_ENV === 'development') {
            console.log("[SCF:OHUG filter]", {
              hint_raw: xfmrOHUGHint,
              hint_norm: need,
              svc_type: v.svc_type,
              svc_phase: v.svc_phase,
              svc_volts: v.svc_volts,
              svc_amps: v.svc_amps,
              baseCount: base.length,
              options: ohug,
            });
          }
        }
      } catch (err) {
        if (!cancelled) {
          setOptMsg("Options fetch failed.");
          setOhugOptions([]);
          setXfmrOHUG(null);
          console.warn("[SCF:OHUG fetch error]", err?.message || err);
        }
      } finally {
        if (!cancelled) setOptLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    safeCategory,
    v.svc_type,
    v.svc_phase,
    v.svc_volts,
    v.svc_amps,
    v.svc_hp,
    selectedPoint?.x,
    selectedPoint?.y,
    selectedPoint?.wkid,
    xfmrOHUGHint,
  ]);

  /*
   * Find the exact service assembly that matches user's full configuration
   * All selections (phase, volts, amps/HP, OH/UG) must match
   * This ID gets sent to backend for cost calculation
   * Irrigation: finds service where svc_max_hp >= requested HP
   * Residential/Commercial: exact amps match
   */
  const matchedService = useMemo(() => {
    const isIrrigation = safeCategory === 'irrigation';
    
    // Basic requirements
    if (!v.svc_type || v.svc_phase == null || !v.svc_volts) return null;
    
    // For irrigation, check HP; for others, check amps
    if (isIrrigation) {
      if (v.svc_hp == null || v.svc_hp <= 0) return null;
    } else {
      if (v.svc_amps == null) return null;
    }

    const hintNorm = normOHUG(xfmrOHUGHint);
    if (!hintNorm) return null;
    if (!v.svc_ohug) return null;
    if (!xfmrOHUG) return null;

    const uSvcOhug = normOHUG(v.svc_ohug);
    const uXf = normOHUG(xfmrOHUG);
    
    // For irrigation, find service where svc_max_hp >= requested HP
    // For others, match exact amps
    const row6 = services.find((s) => {
      if (s.svc_type !== v.svc_type) return false;
      if (Number(s.svc_phase) !== Number(v.svc_phase)) return false;
      if (s.svc_volts !== v.svc_volts) return false;
      if (normOHUG(s.svc_ohug) !== uSvcOhug) return false;
      if (normOHUG(s.xfmr_ohug) !== uXf) return false;
      
      // Final check: HP or amps
      if (isIrrigation) {
        // Find service with smallest svc_max_hp >= requested HP
        return Number(s.svc_max_hp) >= Number(v.svc_hp);
      } else {
        return Number(s.svc_amps) === Number(v.svc_amps);
      }
    });
    
    return row6 || null;
  }, [
    services,
    safeCategory,
    v.svc_type,
    v.svc_phase,
    v.svc_volts,
    v.svc_amps,
    v.svc_hp,
    v.svc_ohug,
    xfmrOHUG,
    xfmrOHUGHint,
  ]);

  return {
    services,
    loading,
    phases,
    volts,
    amps,
    ohugOptions,
    xfmrOHUG,
    optLoading,
    optMsg,
    matchedService,
    normOHUG,
  };
}
